import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { api, ApiError } from "../api";

type Registro = {
  id: number; usuarioId: number | null; usuarioNombre: string | null; tabla: string;
  registroId: number | null; accion: string; valoresAntes: any; valoresDespues: any;
  ip: string | null; creadoEn: string;
};

function accionBadge(accion: string) {
  if (accion.includes("crear")) return "bg-accent-light text-accent-dark";
  if (accion.includes("eliminar") || accion.includes("fallido")) return "bg-red-50 text-red-700";
  if (accion.includes("editar")) return "bg-amber-50 text-amber-700";
  return "bg-slate-100 text-slate-600";
}

export function Auditoria() {
  const [registros, setRegistros] = useState<Registro[]>([]);
  const [tablaFiltro, setTablaFiltro] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detalle, setDetalle] = useState<Registro | null>(null);

  const cargar = async () => {
    setCargando(true);
    setError(null);
    try {
      const query = tablaFiltro ? `?tabla=${encodeURIComponent(tablaFiltro)}` : "";
      setRegistros(await api.get(`/auditoria${query}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la bitácora.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [tablaFiltro]);

  const tablasConocidas = ["usuarios", "roles", "empresas", "parametros_sistema", "catalogos"];

  return (
    <Shell titulo="Auditoría (bitácora)" breadcrumb="Core / Auditoría">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-textmuted">Registro inmutable de cambios, requerido por SIG-ARQ-001 para trazabilidad y control interno.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setTablaFiltro("")} className={`badge border ${tablaFiltro === "" ? "bg-navy text-white border-navy" : "bg-white border-borderc text-slate-600"}`}>Todas</button>
        {tablasConocidas.map((t) => (
          <button key={t} onClick={() => setTablaFiltro(t)} className={`badge border ${tablaFiltro === t ? "bg-navy text-white border-navy" : "bg-white border-borderc text-slate-600"}`}>{t}</button>
        ))}
      </div>

      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

      <div className="card overflow-hidden">
        {cargando ? (
          <p className="p-6 text-sm text-textmuted">Cargando...</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-borderc">
                <th className="table-th">Fecha</th>
                <th className="table-th">Usuario</th>
                <th className="table-th">Tabla</th>
                <th className="table-th">Acción</th>
                <th className="table-th">Registro</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {registros.map((r) => (
                <tr key={r.id} className="border-b border-borderc last:border-0">
                  <td className="table-td whitespace-nowrap text-xs text-textmuted">{r.creadoEn}</td>
                  <td className="table-td">{r.usuarioNombre || "—"}</td>
                  <td className="table-td font-mono text-xs">{r.tabla}</td>
                  <td className="table-td"><span className={`badge ${accionBadge(r.accion)}`}>{r.accion}</span></td>
                  <td className="table-td text-xs text-textmuted">#{r.registroId ?? "—"}</td>
                  <td className="table-td text-right">
                    <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => setDetalle(r)}>Ver detalle</button>
                  </td>
                </tr>
              ))}
              {registros.length === 0 && (
                <tr><td colSpan={6} className="table-td text-center text-textmuted py-8">Sin eventos registrados.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {detalle && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          <div className="card w-full max-w-lg p-6 space-y-3 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">
              {detalle.tabla} · {detalle.accion} · #{detalle.registroId ?? "—"}
            </h3>
            <div className="text-xs text-textmuted">{detalle.creadoEn} · {detalle.usuarioNombre || "sistema"} · IP {detalle.ip || "—"}</div>
            {detalle.valoresAntes && (
              <div>
                <div className="text-[11px] font-semibold uppercase text-textmuted mb-1">Antes</div>
                <pre className="bg-slate-50 rounded-md p-3 text-xs overflow-x-auto">{JSON.stringify(detalle.valoresAntes, null, 2)}</pre>
              </div>
            )}
            {detalle.valoresDespues && (
              <div>
                <div className="text-[11px] font-semibold uppercase text-textmuted mb-1">Después</div>
                <pre className="bg-slate-50 rounded-md p-3 text-xs overflow-x-auto">{JSON.stringify(detalle.valoresDespues, null, 2)}</pre>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <button className="btn-ghost" onClick={() => setDetalle(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </Shell>
  );
}
